import { createElement, forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * Dado pericial em mono (ADR-0065). É a regra mais repetida do Design System:
 * CNJ, LR, datas, códigos, contadores e identificadores **sempre** em
 * monoespaçada — o alinhamento de dígito é o que permite comparar duas linhas de
 * uma tabela sem lê-las. Texto corrido continua em Barlow.
 *
 * Existe como primitivo para que a regra pare de viajar como `font-mono` ad hoc
 * espalhado pelas telas, que é como está hoje.
 */

export type MonoDataElement = 'span' | 'div' | 'p' | 'dd' | 'dt' | 'time' | 'code';

export interface MonoDataProps extends HTMLAttributes<HTMLElement> {
  as?: MonoDataElement;
  variant?: 'default' | 'muted' | 'emphasis' | 'label';
  /** Só faz sentido com `as="time"`; é o que torna a data legível por máquina. */
  dateTime?: string;
}

const VARIANTES: Record<NonNullable<MonoDataProps['variant']>, string> = {
  default: 'text-body-sm text-fg',
  muted: 'text-body-sm text-fg-muted',
  /** Valor que a tela existe para mostrar (LR do cabeçalho, total do extrato). */
  emphasis: 'text-body-md text-fg',
  /** Eyebrow/contador em caixa alta, como "0 ATIVOS · 0 AGUARDAM PRAZO". */
  label: 'text-mono-label uppercase text-fg-dim',
};

export const MonoData = forwardRef<HTMLElement, MonoDataProps>(function MonoData(
  { as = 'span', variant = 'default', className, ...props },
  ref,
) {
  return createElement(as, {
    ref,
    'data-mono-data': variant,
    className: cn('font-mono', VARIANTES[variant], className),
    ...props,
  });
});
